import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Select,
  Tag,
  Row,
  Col,
  Space,
  Typography,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import Card from 'components/card';
import { useMarketplaces } from 'services/MarketplaceService';
import { useStoresByMarketplace } from 'services/StoreService';
import { useDrivers } from 'services/DriverService';
import { Paquer } from 'interfaces/paquer';
import CreateCollectionModal from './CreateCollectionModal';
import CollectionDetailModal from './CollectionDetailModal';
import {
  CollectionItem,
  useGetCollections,
  useCancelCollection,
  CollectionStatus,
} from 'services/CollectionsService';

const { Text } = Typography;

const STATUS_LABELS: Record<CollectionStatus, string> = {
  pending_collection: 'Pendiente',
  in_transit_to_store: 'En tránsito',
  collected: 'Colectada',
  received: 'Completada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS: Record<CollectionStatus, string> = {
  pending_collection: 'blue',
  in_transit_to_store: 'cyan',
  collected: 'orange',
  received: 'green',
  cancelled: 'red',
};

const STATUS_FILTER_OPTIONS: {
  label: string;
  value: CollectionStatus | number;
}[] = [
  { label: 'Pendiente', value: 'pending_collection' },
  { label: 'En curso', value: 'in_transit_to_store' },
  { label: 'Completada', value: 'received' },
  { label: 'Cancelada', value: 'cancelled' },
  { label: 'Colectada', value: 'collected' },
];

const CollectionList: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [detailCollectionId, setDetailCollectionId] = useState<number | null>(
    null,
  );
  const [marketplaceFilter, setMarketplaceFilter] = useState<number | null>(
    null,
  );
  const [storeFilter, setStoreFilter] = useState<number | null>(null);
  const [driverFilter, setDriverFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    CollectionStatus | undefined
  >(undefined);

  const { data: marketplaces = [], isLoading: marketplacesLoading } =
    useMarketplaces();
  const { data: stores = [], isLoading: storesLoading } =
    useStoresByMarketplace(marketplaceFilter ?? 0);
  const { data: driversData = [], isLoading: driversLoading } = useDrivers();
  const drivers = driversData as Paquer[];

  const { mutate: cancelCollection, isPending: cancelling } =
    useCancelCollection();

  const handleCancel = (collectionId: number) => {
    cancelCollection(collectionId, {
      onSuccess: () => message.success('Colecta cancelada'),
      onError: () => message.error('Error al cancelar la colecta'),
    });
  };

  const {
    data: collections,
    isLoading: collectionsLoading,
    isFetching: collectionsRefetching,
    refetch: refetchCollections,
  } = useGetCollections({
    q: driverFilter
      ? drivers.find((d) => d.id === driverFilter)?.name
      : undefined,
    page: 1,
    pageSize: 100,
    status: statusFilter,
  });

  const marketplaceOptions = [
    ...marketplaces.map((m) => ({ label: m.name, value: m.id })),
  ];

  const storeOptions = [...stores.map((s) => ({ label: s.name, value: s.id }))];

  const driverOptions = [
    ...drivers.map((d) => ({ label: `${d.name} ${d.lastName}`, value: d.id })),
  ];

  const handleMarketplaceChange = (value: number | null) => {
    setMarketplaceFilter(value);
    setStoreFilter(null);
  };

  const columns: TableColumnsType<CollectionItem> = [
    {
      title: '#',
      dataIndex: 'collection_request_id',
      key: 'id',
      width: 80,
      align: 'center',
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'Tiendas',
      key: 'stores',
      render: (_: unknown, record: CollectionItem) => (
        <Space direction="vertical" size={4}>
          {record.stores.map((s) => (
            <Space key={s.storeId} size={4}>
              <Tag color="blue" style={{ margin: 0 }}>
                {s.storeName}
              </Tag>
            </Space>
          ))}
        </Space>
      ),
    },
    { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: CollectionStatus) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'right',
      render: (_: unknown, record: CollectionItem) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => setDetailCollectionId(record.collection_request_id)}
          >
            Ver detalle
          </Button>
          <Popconfirm
            title="¿Cancelar colecta?"
            description="Esta acción no se puede deshacer."
            okText="Sí, cancelar"
            cancelText="No"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleCancel(record.collection_request_id)}
            disabled={
              record.status === 'cancelled' || record.status === 'received'
            }
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              disabled={
                record.status === 'cancelled' || record.status === 'received'
              }
              loading={cancelling}
            >
              Cancelar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Versión anterior de Colectas"
        description={
          <>
            Las colectas del modelo anterior, creadas con un driver, estarán
            disponibles hasta el 30 de noviembre para finalizar o cancelar las
            que ya están en curso.
            <br />
            No se recomienda crear nuevas.
            <br />
            Las nuevas colectas se crean desde Rutas, seleccionando un punto de
            colecta, y se consultan en Colectas → En tránsito o Histórico.
          </>
        }
      />
      <Card title="Colectas">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={2} xl={1}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => refetchCollections()}
              loading={collectionsRefetching}
              style={{ width: '100%' }}
            ></Button>
          </Col>

          <Col xs={24} sm={12} lg={4} xl={5}>
            <Select
              style={{ width: '100%' }}
              options={marketplaceOptions}
              value={marketplaceFilter}
              onChange={handleMarketplaceChange}
              placeholder="Filtrar por marketplace"
              loading={marketplacesLoading}
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={4} xl={5}>
            <Select
              style={{ width: '100%' }}
              options={storeOptions}
              value={storeFilter}
              onChange={setStoreFilter}
              placeholder="Filtrar por tienda"
              loading={storesLoading}
              disabled={!marketplaceFilter}
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={4} xl={5}>
            <Select
              style={{ width: '100%' }}
              options={driverOptions}
              placeholder="Filtrar por driver"
              value={driverFilter}
              onChange={setDriverFilter}
              loading={driversLoading}
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={4} xl={5}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filtrar por estado"
              options={STATUS_FILTER_OPTIONS}
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Col>
          <Col
            xs={24}
            sm={12}
            lg={5}
            xl={3}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
              style={{ width: '100%' }}
            >
              Nueva Colecta
            </Button>
          </Col>
        </Row>
        <Table
          dataSource={collections.data || []}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </Card>
      <CreateCollectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      <CollectionDetailModal
        collectionId={detailCollectionId}
        open={!!detailCollectionId}
        onClose={() => setDetailCollectionId(null)}
      />
    </div>
  );
};

export default CollectionList;
